import React, { memo, useCallback } from 'react'
import moment from 'moment'
import styled from 'styled-components'
import { rem } from 'polished'

import { useAppDispatch } from 'hooks/reduxHooks'
import { accountAccessShowLogin } from 'actions/UiActions'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useBedbankSelectDates from 'hooks/OfferPage/useBedbankSelectDates'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import WalledContent from 'components/Common/WalledContent'
import PriceTagIcon from 'components/Luxkit/Icons/custom/PriceTagIcon'
import Caption from 'components/Luxkit/Typography/Caption'
import TextButton from 'components/Luxkit/Button/TextButton'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import BedbankSelectDates from './BedbankSelectDates'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import BodyText from 'components/Luxkit/Typography/BodyText'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import { getPlural } from 'lib/pluralize'

const PackagePriceSection = styled.div`
  text-align: center;
  display: flex;
  flex: 1;
`

const StyledButton = styled(TextButton)`
  margin-top: ${rem(16)};
`

const AuthWrapper = styled.div`
  display: flex;
  flex-direction: column;

  & > * + * {
    margin-top: ${rem(12)};
  }
`

const AuthButtonsWrapper = styled.div`
  & > * + * {
    margin-left: ${rem(8)};
  }
`

const SelectDatesWrapper = styled.div`
  align-self: flex-end;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  text-align: left;
  width: 100%;
`

interface Props {
  checkinDate: moment.Moment;
  checkoutDate: moment.Moment;
  offer: App.BedbankOffer;
  rates: Array<App.BedbankRate>;
  fetchingRates: boolean;
  fetchingFlightPrices?: boolean;
  flightPrice?: number;
  flightsEnabled: boolean;
  isFilterActive: boolean;
  packageView: App.BedbankOfferPackageView;
  isShowingMoreDetails: boolean;
  toggleShowMoreDetails: () => void;
}

function BedbankBookingPane({
  checkinDate,
  checkoutDate,
  offer,
  rates,
  packageView,
  isFilterActive,
  fetchingRates,
  isShowingMoreDetails,
  toggleShowMoreDetails,
}: Props) {
  const validCheckinCheckout = !!(checkinDate && checkoutDate)
  const offerPageDispatch = useOfferPageDispatch()
  const authDispatch = useAppDispatch()
  const rate = Math.round(rates[0]?.discount * 100)
  const onCalendarOpenSelectDates = useBedbankSelectDates()

  const hotelAvailable = packageView?.available
  const canPurchase = rates.length > 0 && packageView?.capacityValid && hotelAvailable && !fetchingRates

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterTypes })
  }, [offerPageDispatch])

  return <PackagePriceSection>
    <WalledContent enforced={offer.walledGarden} wall={(
      <AuthWrapper>
        <BodyTextBlock weight="semi-bold" variant="medium" startIcon={<PriceTagIcon />}>
          {!rate ? 'Members get access to exclusive rates' : `Get ${rate}% off with member prices`}
        </BodyTextBlock>
        <AuthButtonsWrapper>
          <TextButton kind="secondary" onClick={() => authDispatch(accountAccessShowLogin())}>
            Log In
          </TextButton>
          <TextButton kind="primary" onClick={() => authDispatch(accountAccessShowLogin())}>
            Sign up for free
          </TextButton>
        </AuthButtonsWrapper>
      </AuthWrapper>
    )}>
      <SelectDatesWrapper>
        <BodyText variant="medium">
          <SmallToggleButton
            on={isShowingMoreDetails}
            onClick={toggleShowMoreDetails}
          >
            {isShowingMoreDetails ? 'Hide' : 'View'} all details
          </SmallToggleButton>
        </BodyText>
        {!validCheckinCheckout && packageView.capacityValid &&
          <BedbankSelectDates />
        }

        {!canPurchase && (
          <div>
            {validCheckinCheckout && packageView.capacityValid && (
              <BodyText variant="medium">
                <Caption variant="large">This {getPlural(offer.saleUnit)} is not available for your selected dates.</Caption>
                <StyledButton
                kind="primary"
                onClick={onCalendarOpenSelectDates}
                data-testid="select-your-dates-button"
                fit="full-width"
              >
                  Change your dates
                </StyledButton>
              </BodyText>
            )}

            {hotelAvailable && !packageView.capacityValid && (
              <BodyText variant="medium">
                <Caption variant="large">
                  Max number of guests exceeded.
                  <br />
                  <TextButton
              kind="tertiary"
              onClick={onToggleFilter}
              data-filter="capacity">
                    Add another {offer.saleUnit}
                  </TextButton>
                </Caption>

              </BodyText>
            )}

            {!hotelAvailable && (
              <Caption variant="large">
                Sold out for your selected dates.
              </Caption>
            )}
          </div>
        )}
      </SelectDatesWrapper>
      <LoadingIndicator floating opaque visible={isFilterActive || fetchingRates} />
    </WalledContent>
  </PackagePriceSection>
}

export default memo(BedbankBookingPane)
